import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./dischargeSummary.scss";
import { HMS_IFRAME_MESSAGES } from "./iframeMessages";

const DISCHARGE_SUMMARY_URL =
  process.env.REACT_APP_DISCHARGE_SUMMARY_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:5173/discharge-summary" : "/discharge-summary-module");
const DISCHARGE_SUMMARY_ORIGIN = DISCHARGE_SUMMARY_URL.startsWith("http")
  ? new URL(DISCHARGE_SUMMARY_URL).origin
  : window.location.origin;

const DISCHARGE_SUMMARY_ROUTE_LABELS = {
  "/": "Discharge Summary",
  "/hysteroscopy": "Hysteroscopy Discharge Summary",
  "/cervical-cerclage": "Cervical Cerclage Discharge Summary",
  "/d-and-c": "D&C Discharge Summary",
  "/general": "General Discharge Summary",
  "/other": "Manual Discharge Summary",
};

const LOADER_FALLBACK_MS = 3000;

function parseStoredUser(userdata) {
  if (!userdata) return null;
  try {
    return JSON.parse(userdata);
  } catch (e) {
    return null;
  }
}

function DischargeSummary() {
  const iframeRef = useRef(null);
  const readyRef = useRef(false);
  const loaderFallbackRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sideactive = useSelector((state) => state.counter.sidebarnav);
  const [isLoading, setIsLoading] = useState(true);

  const dischargeSummaryPath = useMemo(() => {
    return location.pathname.replace("/discharge-summary", "") || "/";
  }, [location.pathname]);

  const iframeSrc = `${DISCHARGE_SUMMARY_URL.replace(/\/$/, "")}${dischargeSummaryPath}${location.search}`;
  const loadingLabel = DISCHARGE_SUMMARY_ROUTE_LABELS[dischargeSummaryPath] || "Discharge Summary";

  useEffect(() => {
    readyRef.current = false;
    if (loaderFallbackRef.current) {
      window.clearTimeout(loaderFallbackRef.current);
      loaderFallbackRef.current = null;
    }
    setIsLoading(true);
  }, [iframeSrc]);

  const sendAuthToIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return false;

    const token = localStorage.getItem("token");
    const userdata = localStorage.getItem("userdata");
    const branch = localStorage.getItem("selectedBranch") || localStorage.getItem("branch");
    if (!token) return false;

    try {
      iframe.contentWindow.postMessage(
        {
          type: HMS_IFRAME_MESSAGES.authToken,
          token,
          user: parseStoredUser(userdata),
          branch,
        },
        DISCHARGE_SUMMARY_ORIGIN
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const handleReady = (event) => {
      if (
        event.origin === DISCHARGE_SUMMARY_ORIGIN &&
        event.source === iframeRef.current?.contentWindow
      ) {
        if (event.data?.type === HMS_IFRAME_MESSAGES.ready) {
          readyRef.current = true;
          sendAuthToIframe();
          if (loaderFallbackRef.current) {
            window.clearTimeout(loaderFallbackRef.current);
            loaderFallbackRef.current = null;
          }
          setIsLoading(false);
        }

        if (event.data?.type === HMS_IFRAME_MESSAGES.authRequest) {
          sendAuthToIframe();
        }

        if (event.data?.type === HMS_IFRAME_MESSAGES.navigate && event.data?.path) {
          navigate(event.data.path);
        }

        if (event.data?.type === HMS_IFRAME_MESSAGES.close) {
          navigate("/discharge-summary", { replace: true });
        }
      }
    };

    window.addEventListener("message", handleReady);
    return () => window.removeEventListener("message", handleReady);
  }, [navigate]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const sendAuth = () => {
      sendAuthToIframe();
      if (loaderFallbackRef.current) window.clearTimeout(loaderFallbackRef.current);
      loaderFallbackRef.current = window.setTimeout(() => {
        if (!readyRef.current) setIsLoading(false);
      }, LOADER_FALLBACK_MS);
    };

    iframe.addEventListener("load", sendAuth);
    return () => {
      iframe.removeEventListener("load", sendAuth);
      if (loaderFallbackRef.current) {
        window.clearTimeout(loaderFallbackRef.current);
        loaderFallbackRef.current = null;
      }
    };
  }, [iframeSrc]);

  useEffect(() => {
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      sendAuthToIframe();
      if (attempts >= 20) window.clearInterval(timer);
    }, 300);

    sendAuthToIframe();
    return () => window.clearInterval(timer);
  }, [iframeSrc]);

  return (
    <div className={`discharge-summary-container ${sideactive ? "sidebar-open" : "sidebar-closed"}`}>
      {isLoading && (
        <div className="discharge-summary-loader" role="status" aria-live="polite" aria-label={`Loading ${loadingLabel}`}>
          <div className="discharge-summary-loader-card">
            <span className="discharge-summary-loader-spinner" aria-hidden="true" />
            <strong>{`Loading ${loadingLabel}...`}</strong>
            <span>Please wait while the discharge workspace is prepared.</span>
          </div>
        </div>
      )}
      <iframe ref={iframeRef} src={iframeSrc} title="Discharge Summary Module" className="discharge-summary-iframe" />
    </div>
  );
}

export default DischargeSummary;
